"use client";

/**
 * PostHog web analytics for the public marketing surface (kreayt.ai).
 *
 * Captures ONLY on the public marketing hosts listed in CAPTURE_HOSTS so
 * internal app usage on dev.kreayt.ai / oneshot-dev.vizyx.net / Vercel
 * previews / localhost never inflates visitor numbers. Set
 * NEXT_PUBLIC_POSTHOG_ALLOW_ALL_HOSTS=1 to capture everywhere (local testing).
 *
 * Events go through the same-origin /ingest reverse proxy (see next.config.ts
 * rewrites + the /ingest pass-through in middleware.ts) so ad blockers do not
 * see a third-party analytics domain.
 */

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { ReactNode, useEffect } from "react";

const CAPTURE_HOSTS = new Set(["kreayt.ai", "www.kreayt.ai"]);

function shouldCapture(): boolean {
  if (typeof window === "undefined") return false;
  if (process.env.NEXT_PUBLIC_POSTHOG_ALLOW_ALL_HOSTS === "1") return true;
  return CAPTURE_HOSTS.has(window.location.hostname);
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key || !shouldCapture()) return;
    if (posthog.__loaded) return;

    posthog.init(key, {
      api_host: "/ingest",
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.posthog.com",
      defaults: "2026-06-25",
      // SPA-aware pageviews (fires on Next.js client-side navigation too)
      capture_pageview: "history_change",
      capture_pageleave: true,
      autocapture: true,
      // Session replay is toggled per-project in the PostHog dashboard; when on,
      // mask every input so form fields (email, name) are never recorded.
      session_recording: {
        maskAllInputs: true,
      },
      person_profiles: "identified_only",
    });
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
